import { useMemo, useRef } from "react";
import { newIdempotencyKey } from "./idempotency";
import { ApiProblem } from "./problem";

/** Refusals that are not definite: the same request may succeed later, so the key must be kept. */
const KEEP_KEY_STATUSES: ReadonlySet<number> = new Set([408, 429]);

/**
 * Whether `error` is a definite refusal (a 4xx problem other than 408, 429, `idempotency_in_flight` and `update_in_flight`).
 * The shared idempotency middleware replays every non-5xx response for the key's TTL, so resubmitting
 * an unchanged form with the same key would only replay the refusal: such an error must reset the key.
 */
export function isDefiniteRefusal(error: unknown): boolean {
  if (!(error instanceof ApiProblem)) return false;
  if (error.status < 400 || error.status >= 500) return false;
  if (KEEP_KEY_STATUSES.has(error.status)) return false;
  // "Still being recorded": the same request must be resent unchanged (review R-W6).
  return error.code !== "idempotency_in_flight" && error.code !== "update_in_flight";
}

/** JSON with object keys sorted, so the same values always give the same fingerprint. */
function fingerprint(value: unknown): string {
  return JSON.stringify(value, (_k, v: unknown) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : v,
  );
}

export interface IdempotencyKeys {
  /**
   * The key for submitting `payload`: the same key while the payload is unchanged (so resubmitting after
   * a network error or a lost reply never applies the write twice), a new key once the values change.
   */
  keyFor(payload: unknown): string;
  /** Forget the key (call after success): the next submission is a new intent with a new key. */
  reset(): void;
  /**
   * Call from the mutation's `onError`: resets the key after a definite 4xx refusal (see
   * {@link isDefiniteRefusal}); keeps it after network errors, 5xx, 408, 429 and `idempotency_in_flight`.
   */
  onError(error: unknown): void;
}

/** One Idempotency-Key per form or dialog instance, held for the life of the component. */
export function useIdempotencyKey(): IdempotencyKeys {
  const current = useRef<{ fp: string; key: string } | null>(null);
  return useMemo(
    () => ({
      keyFor(payload: unknown) {
        const fp = fingerprint(payload);
        if (!current.current || current.current.fp !== fp) current.current = { fp, key: newIdempotencyKey() };
        return current.current.key;
      },
      reset() {
        current.current = null;
      },
      onError(error: unknown) {
        if (isDefiniteRefusal(error)) current.current = null;
      },
    }),
    [],
  );
}
